import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Create Draft Sales Order Step (workflow-native approval flow)
 *
 * Relational counterpart to `create_sales_order_draft`: extracts purchase-order
 * data from classified documents and creates `ar_draft_sales_orders` +
 * `ar_draft_sales_order_line_items` (relational lines, stable ids) instead of
 * the JSONB `ar_staged_customer_purchase_orders`.
 *
 * This step does **extraction + persist only** — it does NOT resolve the
 * customer or match products. In the OMS v3 routing flow it runs in the central
 * routing org, where the order's selling org isn't known yet, so org-scoped
 * matching would scope to the wrong org. Customer, product, and ship-to
 * matching are deferred to the target org's `resolve_draft_details` step, which
 * runs after the draft is routed + reassigned to its selling org.
 *
 * Backend-only step (requires DB access); handler in
 * backend/src/workflows/steps/order-management/.
 */
export const createDraftSalesOrderStep = defineStepConfig({
  type: "create_draft_sales_order",
  name: "Create Draft Sales Order",
  description:
    "Extract purchase order data from classified documents and create relational draft sales orders",
  category: "Data Access",
  libraryModule: "OrderManagement",
  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email containing purchase order documents",
    ),
    documents: z
      .array(entityOfType(SystemEntity.CORE_DOCUMENT))
      .optional()
      .describe(
        "Filtered document IDs to process (from extract step). If omitted, all PO documents from the email are processed.",
      ),
    originType: z
      .string()
      .optional()
      .describe(
        'Email origin classification: "external", "self_sent", or "reply_to_outbound"',
      ),
    conversationId: z
      .string()
      .optional()
      .describe(
        "Microsoft Graph conversation/thread ID for tracing replies to orders",
      ),
  }),

  outputSchema: z.object({
    draftSalesOrderIds: z
      .array(z.string().uuid())
      .describe("UUIDs of all draft sales orders created from this email"),
    firstDraftSalesOrderId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "UUID of the first draft sales order (convenience output for single-PO flows). Undefined when no PO documents were found.",
      ),
    inboundEmailId: z
      .string()
      .uuid()
      .describe("UUID of the inbound email that was processed"),
    entityType: z
      .string()
      .optional()
      .describe(
        "Entity type of the related order (reply flow) — reserved, not yet populated",
      ),
    entityId: z
      .string()
      .optional()
      .describe("Entity ID of the related order (reply flow) — reserved"),
    customerReplyText: z
      .string()
      .optional()
      .describe("Stripped customer reply text (reply flow) — reserved"),
  }),

  configSchema: z.object({
    skipClassificationCheck: z
      .boolean()
      .optional()
      .describe(
        "Skip document classification checks (used for legacy processing paths)",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 2,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
    nonRetryableErrorTypes: ["ValidationError", "SchemaValidationError"],
  },
});
